<template>
  <div class="timer-container">
    <h1>Climax Countdown</h1>
    
    <!-- Configuration Section -->
    <div class="config-section" v-if="!isRunning">
      <div class="help-text">
        Each round is one minute: 40 seconds action + 20 seconds pause<br>
        Stimulate by circling every tick.
      </div>
      <div class="config-item">
        <label for="rounds">Number of Rounds:</label>
        <input 
          id="rounds"
          type="number" 
          v-model.number="totalRounds" 
          min="3" 
          max="30"
          :disabled="isRunning"
        />
      </div>
      <div class="config-item">
        <label for="firstTicks">First Round Ticks:</label>
        <div class="input-with-info">
          <input 
            id="firstTicks"
            type="number" 
            v-model.number="firstRoundTicks" 
            min="1" 
            max="1000"
            :disabled="isRunning"
          />
          <span class="interval-info">every {{ Math.round(40000 / firstRoundTicks) }}ms</span>
        </div>
      </div>
      <div class="config-item">
        <label for="lastTicks">Last Round Ticks:</label>
        <div class="input-with-info">
          <input 
            id="lastTicks"
            type="number" 
            v-model.number="lastRoundTicks" 
            min="1" 
            max="1000"
            :disabled="isRunning"
          />
          <span class="interval-info">every {{ Math.round(40000 / lastRoundTicks) }}ms</span>
        </div>
      </div>
      <button @click="startTimer" :disabled="isRunning || totalRounds < 3" class="start-btn">
        Start Timer
      </button>
    </div>

    <!-- Timer Display -->
    <div class="timer-display" v-if="isRunning || isPaused">
      <div class="round-info">
        <h2>Round {{ currentRound }} of {{ totalRounds }}</h2>
        <div class="phase">{{ currentPhase }}</div>
        <div class="tick-info" v-if="currentPhase === 'Metronome'">
          Ticks: {{ tickCount }} / {{ totalTicks }}
        </div>
      </div>
      
      <div class="time-display">
        <div class="time" v-if="currentPhase !== 'Climax'">{{ formatTime(timeRemaining) }}</div>
        <div class="time climax-time" v-else>CLIMAX</div>
        <div class="progress-bar">
          <div class="progress" :style="{ width: progressPercentage + '%' }"></div>
        </div>
      </div>

      <div class="controls">
        <button @click="pauseTimer" v-if="isRunning" class="pause-btn">Pause</button>
        <button @click="resumeTimer" v-if="isPaused" class="resume-btn">Resume</button>
        <button @click="nextRound" v-if="isRunning && currentRound < totalRounds" class="next-btn">Next Round</button>
        <button @click="stopTimer" class="stop-btn">Stop</button>
      </div>
    </div>

    <!-- Results -->
    <div class="results" v-if="isCompleted">
      <h2>Timer Complete!</h2>
      <p>All {{ totalRounds }} rounds have been completed.</p>
      <button @click="resetTimer" class="reset-btn">Start New Timer</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetronomeTimer',
  data() {
    return {
      totalRounds: 15,
      firstRoundTicks: 8,
      lastRoundTicks: 133,
      currentRound: 1,
      timeRemaining: 0,
      isRunning: false,
      isPaused: false,
      isCompleted: false,
      currentPhase: '',
      intervalId: null,
      tickIntervalId: null,
      audioContext: null,
      oscillator: null,
      tickCount: 0,
      totalTicks: 0,
      tickProgress: 0,
      tickStartTime: 0,
      currentTickInterval: 0,
      animationFrameId: null,
      wakeLock: null
    }
  },
  computed: {
    progressPercentage() {
      if (this.currentPhase === 'Metronome') {
        return this.tickProgress;
      } else if (this.currentPhase === 'Pause') {
        return ((20 - this.timeRemaining) / 20) * 100;
      } else if (this.currentPhase === 'Climax') {
        return this.tickProgress;
      }
      return 0;
    }
  },
  methods: {
    startTimer() {
      this.isRunning = true;
      this.isPaused = false;
      this.isCompleted = false;
      this.currentRound = 1;
      this.requestWakeLock();
      this.startRound();
    },
    
    startRound() {
      this.currentPhase = 'Metronome';
      this.timeRemaining = 40;
      this.tickCount = 0;
      this.tickProgress = 0;
      this.calculateTotalTicks();
      this.startMetronome();
      this.startCountdown();
    },
    
    calculateTotalTicks() {
      let tickCount;
      if (this.currentRound === 1) {
        tickCount = this.firstRoundTicks;
      } else if (this.currentRound === this.totalRounds) {
        tickCount = this.lastRoundTicks;
      } else {
        // Linear interpolation between first and last round tick counts
        const progress = (this.currentRound - 1) / (this.totalRounds - 1);
        tickCount = this.firstRoundTicks + (progress * (this.lastRoundTicks - this.firstRoundTicks));
      }
      
      // Round to nearest integer
      this.totalTicks = Math.round(tickCount);
    },
    
    startMetronome() {
      // Calculate tick interval based on round
      let tickInterval;
      if (this.currentRound === 1) {
        // Calculate interval based on first round ticks (40 seconds / number of ticks)
        tickInterval = 40000 / this.firstRoundTicks;
      } else if (this.currentRound === this.totalRounds) {
        // Calculate interval based on last round ticks (40 seconds / number of ticks)
        tickInterval = 40000 / this.lastRoundTicks;
      } else {
        // Linear interpolation between first and last round tick counts
        const progress = (this.currentRound - 1) / (this.totalRounds - 1);
        const tickCount = this.firstRoundTicks + (progress * (this.lastRoundTicks - this.firstRoundTicks));
        tickInterval = 40000 / tickCount;
      }
      
      this.currentTickInterval = tickInterval;
      
      // Start the first tick immediately
      this.playTick();
      
      // Set up the interval for subsequent ticks
      this.tickIntervalId = setInterval(() => {
        this.playTick();
      }, tickInterval);
    },
    
    playTick() {
      // Stop any existing animation immediately
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Reset progress to 0 immediately when tick plays
      this.tickProgress = 0;
      
      // Play the tick sound
      try {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.1);
      } catch (error) {
        console.log('Audio not available:', error);
      }
      
      // Increment tick count
      this.tickCount++;
      
      // Start fresh progress animation with precise timing
      this.tickStartTime = performance.now();
      
      const updateProgress = () => {
        if (!this.isRunning || this.isPaused) {
          this.animationFrameId = null;
          return;
        }
        
        const elapsed = performance.now() - this.tickStartTime;
        const progress = (elapsed / this.currentTickInterval) * 100;
        
        if (progress >= 100) {
          this.tickProgress = 100;
          this.animationFrameId = null;
          console.log(`Progress reached 100% at ${elapsed}ms (expected: ${this.currentTickInterval}ms)`);
        } else {
          this.tickProgress = progress;
          this.animationFrameId = requestAnimationFrame(updateProgress);
        }
      };
      
      this.animationFrameId = requestAnimationFrame(updateProgress);
    },
    
    playWarningBeep() {
      try {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        // Lower frequency for warning beep
        oscillator.frequency.setValueAtTime(400, this.audioContext.currentTime);
        oscillator.type = 'sine';
        
        // Softer and longer beep
        gainNode.gain.setValueAtTime(0.05, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.8);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.8);
      } catch (error) {
        console.log('Audio not available:', error);
      }
    },
    
    startCountdown() {
      this.intervalId = setInterval(() => {
        this.timeRemaining--;
        
        // Play warning beep in the last second of pause
        if (this.currentPhase === 'Pause' && this.timeRemaining === 1) {
          this.playWarningBeep();
        }
        
        if (this.timeRemaining <= 0) {
          this.endPhase();
        }
      }, 1000);
    },
    
    endPhase() {
      clearInterval(this.intervalId);
      clearInterval(this.tickIntervalId);
      
      if (this.currentPhase === 'Metronome') {
        // Check if this is the last round
        if (this.currentRound === this.totalRounds) {
          // Last round continues indefinitely - no pause phase
          this.currentPhase = 'Climax';
          this.timeRemaining = 0; // No countdown for last round
          // Keep the metronome running
          this.startMetronome();
        } else {
          // Start pause phase for non-last rounds
          this.currentPhase = 'Pause';
          this.timeRemaining = 20;
          this.startCountdown();
        }
      } else if (this.currentPhase === 'Pause') {
        // Move to next round
        this.currentRound++;
        this.startRound();
      }
    },
    
    pauseTimer() {
      this.isRunning = false;
      this.isPaused = true;
      clearInterval(this.intervalId);
      clearInterval(this.tickIntervalId);
      
      // Cancel any ongoing animation
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    
    resumeTimer() {
      this.isRunning = true;
      this.isPaused = false;
      this.startCountdown();
      this.startMetronome();
    },
    
    nextRound() {
      // Stop current round
      clearInterval(this.intervalId);
      clearInterval(this.tickIntervalId);
      
      // Cancel any ongoing animation
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Move to next round
      if (this.currentRound < this.totalRounds) {
        this.currentRound++;
        this.startRound();
      }
    },
    
    stopTimer() {
      this.isRunning = false;
      this.isPaused = false;
      this.isCompleted = false;
      clearInterval(this.intervalId);
      clearInterval(this.tickIntervalId);
      
      // Cancel any ongoing animation
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Release wake lock
      this.releaseWakeLock();
      
      this.currentRound = 1;
      this.timeRemaining = 0;
      this.currentPhase = '';
      this.tickCount = 0;
      this.totalTicks = 0;
      this.tickProgress = 0;
      this.tickStartTime = 0;
      this.currentTickInterval = 0;
    },
    
    completeTimer() {
      this.isRunning = false;
      this.isPaused = false;
      this.isCompleted = true;
      clearInterval(this.intervalId);
      clearInterval(this.tickIntervalId);
      
      // Cancel any ongoing animation
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Release wake lock
      this.releaseWakeLock();
    },
    
    resetTimer() {
      this.stopTimer();
      this.totalRounds = 15;
      this.firstRoundTicks = 8;
      this.lastRoundTicks = 133;
      this.tickCount = 0;
      this.totalTicks = 0;
      this.tickProgress = 0;
      this.tickStartTime = 0;
      this.currentTickInterval = 0;
      this.animationFrameId = null;
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    async requestWakeLock() {
      try {
        if ('wakeLock' in navigator) {
          this.wakeLock = await navigator.wakeLock.request('screen');
          console.log('Screen wake lock acquired');
          
          // Handle wake lock release (e.g., when user switches tabs)
          this.wakeLock.addEventListener('release', () => {
            console.log('Screen wake lock released');
            this.wakeLock = null;
          });
        } else {
          console.log('Screen Wake Lock API not supported');
        }
      } catch (err) {
        console.log('Failed to acquire wake lock:', err);
      }
    },
    
    releaseWakeLock() {
      if (this.wakeLock) {
        this.wakeLock.release();
        this.wakeLock = null;
        console.log('Screen wake lock released manually');
      }
    }
  },
  
  beforeUnmount() {
    clearInterval(this.intervalId);
    clearInterval(this.tickIntervalId);
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.releaseWakeLock();
  }
}
</script>

<style scoped>
.timer-container {
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 20px;
  text-align: center;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  background-image: url('@/assets/favicon.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.timer-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('@/assets/favicon.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.3;
  z-index: -1;
}

h1 {
  color: #2c3e50;
  margin-bottom: 30px;
}

.config-section {
  background: rgba(248, 249, 250, 0.5);
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.help-text {
  text-align: center;
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 20px;
  padding: 10px;
  background: #e9ecef;
  border-radius: 5px;
  font-style: italic;
}

.config-item {
  margin-bottom: 15px;
}

.config-item label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #2c3e50;
}

.config-item input {
  width: 100px;
  padding: 8px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  text-align: center;
}

.config-item input:focus {
  outline: none;
  border-color: #42b983;
}

.input-with-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.interval-info {
  font-size: 14px;
  color: #6c757d;
  font-style: italic;
  min-width: 120px;
}


.start-btn, .pause-btn, .resume-btn, .next-btn, .stop-btn, .reset-btn {
  padding: 12px 24px;
  margin: 5px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.start-btn {
  background: #42b983;
  color: white;
}

.start-btn:hover:not(:disabled) {
  background: #369870;
}

.pause-btn {
  background: #ffc107;
  color: #212529;
}

.pause-btn:hover {
  background: #e0a800;
}

.resume-btn {
  background: #28a745;
  color: white;
}

.resume-btn:hover {
  background: #218838;
}

.next-btn {
  background: #17a2b8;
  color: white;
}

.next-btn:hover {
  background: #138496;
}

.stop-btn {
  background: #dc3545;
  color: white;
}

.stop-btn:hover {
  background: #c82333;
}

.reset-btn {
  background: #6c757d;
  color: white;
}

.reset-btn:hover {
  background: #5a6268;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.timer-display {
  background: #fff;
  border: 2px solid #42b983;
  border-radius: 10px;
  padding: 40px;
  margin: 20px 0;
  min-width: 500px;
  max-width: 800px;
  width: 90%;
}

.round-info h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.phase {
  font-size: 18px;
  font-weight: bold;
  color: #42b983;
  margin-bottom: 10px;
}

.tick-info {
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 20px;
}

.time-display {
  margin: 20px 0;
}

.time {
  font-size: 72px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 30px;
  font-family: 'Courier New', monospace;
}

.climax-time {
  color: #dc3545;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.progress-bar {
  width: 100%;
  height: 30px;
  background: #e9ecef;
  border-radius: 15px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #42b983, #369870);
}

.controls {
  margin-top: 20px;
}

.results {
  background: #d4edda;
  border: 2px solid #c3e6cb;
  border-radius: 10px;
  padding: 30px;
  margin: 20px 0;
}

.results h2 {
  color: #155724;
  margin-bottom: 15px;
}

.results p {
  color: #155724;
  font-size: 18px;
  margin-bottom: 20px;
}
</style>
